"use client";

import {
  useEffect,
  useRef,
  type MouseEvent,
  type PointerEvent,
  type ReactNode,
} from "react";
import { useLenis } from "lenis/react";

export type MarqueeDirection = "ltr" | "rtl";

export type InfiniteScrollMarqueeProps = {
  children: ReactNode;
  /** `ltr` = left→right; `rtl` = right→left when the page scrolls down */
  direction?: MarqueeDirection;
  /**
   * Continuous drift in px/s. Keep at `0` for drag + scroll only
   * (no auto-slide).
   */
  baseSpeed?: number;
  /**
   * How far the track moves per 1px of page scroll.
   * Higher = more slide travel while scrolling.
   */
  scrollInfluence?: number;
  className?: string;
  trackClassName?: string;
  gapClassName?: string;
  "aria-label"?: string;
};

type DragState = {
  startX: number;
  startOffset: number;
  lastX: number;
  lastTime: number;
};

const CLICK_THRESHOLD_PX = 8;
const COPY_COUNT = 3;
const INERTIA_FRICTION = 0.94;
const INERTIA_STOP_VELOCITY = 0.15;
const MAX_INERTIA_VELOCITY = 4.5;

function normalizeOffset(value: number, cycleWidth: number) {
  if (cycleWidth <= 0) return value;

  let next = value;
  const min = -cycleWidth * 2;
  const max = -cycleWidth;

  while (next < min) next += cycleWidth;
  while (next > max) next -= cycleWidth;

  return next;
}

/**
 * Infinite horizontal track: no autoplay by default.
 * Moves from page scroll (alternating LTR/RTL) and pointer drag with inertia.
 */
export function InfiniteScrollMarquee({
  children,
  direction = "ltr",
  baseSpeed = 0,
  scrollInfluence = 2.4,
  className,
  trackClassName,
  gapClassName = "gap-4 sm:gap-5",
  "aria-label": ariaLabel = "Drag or scroll to explore",
}: InfiniteScrollMarqueeProps) {
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const measureRef = useRef<HTMLDivElement | null>(null);
  const dragStateRef = useRef<DragState | null>(null);
  const pointerIdRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);
  const lastTimeRef = useRef<number | null>(null);
  const offsetRef = useRef(0);
  const cycleWidthRef = useRef(0);
  const isDraggingRef = useRef(false);
  const didDragRef = useRef(false);
  const suppressClickRef = useRef(false);
  const pendingScrollDeltaRef = useRef(0);
  const lastScrollYRef = useRef<number | null>(null);
  const inertiaVelocityRef = useRef(0);
  const usingLenisRef = useRef(false);
  const reduceMotionRef = useRef(false);
  const directionSignRef = useRef(direction === "ltr" ? 1 : -1);

  directionSignRef.current = direction === "ltr" ? 1 : -1;

  useLenis((lenis) => {
    usingLenisRef.current = true;
    const y = lenis.scroll;
    if (lastScrollYRef.current === null) {
      lastScrollYRef.current = y;
      return;
    }
    pendingScrollDeltaRef.current += y - lastScrollYRef.current;
    lastScrollYRef.current = y;
  });

  const applyOffset = (value: number) => {
    const next = normalizeOffset(value, cycleWidthRef.current);
    offsetRef.current = next;
    if (trackRef.current) {
      trackRef.current.style.transform = `translate3d(${next}px, 0, 0)`;
    }
  };

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => {
      reduceMotionRef.current = media.matches;
    };
    sync();
    media.addEventListener("change", sync);
    return () => media.removeEventListener("change", sync);
  }, []);

  // Fallback when Lenis is not mounted (e.g. prefers-reduced-motion).
  useEffect(() => {
    const onScroll = () => {
      if (usingLenisRef.current) return;

      const y = window.scrollY;
      if (lastScrollYRef.current === null) {
        lastScrollYRef.current = y;
        return;
      }
      pendingScrollDeltaRef.current += y - lastScrollYRef.current;
      lastScrollYRef.current = y;
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const element = measureRef.current;
    if (!element) return;

    // Read gap once from computed style; width comes from ResizeObserver
    // contentRect to avoid forced reflow (getBoundingClientRect after writes).
    const style = window.getComputedStyle(element);
    const gap = Number.parseFloat(style.columnGap || style.gap || "0") || 0;

    const applyWidth = (contentWidth: number) => {
      const nextWidth = contentWidth + gap;
      if (nextWidth <= 0) return;

      const hadWidth = cycleWidthRef.current > 0;
      cycleWidthRef.current = nextWidth;

      if (!hadWidth) {
        applyOffset(-nextWidth);
      } else {
        applyOffset(offsetRef.current);
      }
    };

    const observer = new ResizeObserver((entries) => {
      applyWidth(entries[0]?.contentRect.width ?? 0);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [children, direction]);

  useEffect(() => {
    const step = (time: number) => {
      if (lastTimeRef.current === null) {
        lastTimeRef.current = time;
      }

      const deltaMs = Math.min(time - lastTimeRef.current, 40);
      lastTimeRef.current = time;
      const deltaSec = deltaMs / 1000;

      if (!isDraggingRef.current && cycleWidthRef.current > 0) {
        const sign = directionSignRef.current;
        const reduced = reduceMotionRef.current;

        const driftPx =
          reduced || baseSpeed === 0 ? 0 : baseSpeed * sign * deltaSec;

        // Direct scroll-distance mapping — stronger, more controllable travel.
        const scrollDelta = pendingScrollDeltaRef.current;
        pendingScrollDeltaRef.current = 0;
        const scrollPx = reduced ? 0 : scrollDelta * scrollInfluence * sign;

        let next = offsetRef.current + driftPx + scrollPx;

        if (
          !reduced &&
          Math.abs(inertiaVelocityRef.current) > INERTIA_STOP_VELOCITY
        ) {
          next += inertiaVelocityRef.current * deltaMs;
          inertiaVelocityRef.current *= INERTIA_FRICTION;
        } else {
          inertiaVelocityRef.current = 0;
        }

        applyOffset(next);
      } else {
        pendingScrollDeltaRef.current = 0;
      }

      rafRef.current = window.requestAnimationFrame(step);
    };

    rafRef.current = window.requestAnimationFrame(step);
    return () => {
      if (rafRef.current !== null) window.cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      lastTimeRef.current = null;
    };
  }, [baseSpeed, scrollInfluence]);

  const releasePointer = () => {
    if (
      pointerIdRef.current !== null &&
      viewportRef.current?.hasPointerCapture(pointerIdRef.current)
    ) {
      viewportRef.current.releasePointerCapture(pointerIdRef.current);
    }
    pointerIdRef.current = null;
    dragStateRef.current = null;
    isDraggingRef.current = false;
  };

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (cycleWidthRef.current <= 0) return;
    if (event.button !== 0) return;

    pointerIdRef.current = event.pointerId;
    didDragRef.current = false;
    isDraggingRef.current = false;
    inertiaVelocityRef.current = 0;
    dragStateRef.current = {
      startX: event.clientX,
      startOffset: offsetRef.current,
      lastX: event.clientX,
      lastTime: performance.now(),
    };
  };

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (!dragStateRef.current || cycleWidthRef.current <= 0) return;
    if (pointerIdRef.current !== event.pointerId) return;

    const deltaX = event.clientX - dragStateRef.current.startX;

    if (!didDragRef.current) {
      if (Math.abs(deltaX) < CLICK_THRESHOLD_PX) return;
      didDragRef.current = true;
      isDraggingRef.current = true;
      viewportRef.current?.setPointerCapture(event.pointerId);
    }

    const now = performance.now();
    const dt = now - dragStateRef.current.lastTime;
    if (dt > 0) {
      const instantVelocity =
        (event.clientX - dragStateRef.current.lastX) / dt;
      inertiaVelocityRef.current = Math.max(
        -MAX_INERTIA_VELOCITY,
        Math.min(MAX_INERTIA_VELOCITY, instantVelocity),
      );
    }
    dragStateRef.current.lastX = event.clientX;
    dragStateRef.current.lastTime = now;

    applyOffset(dragStateRef.current.startOffset + deltaX);
  };

  const handlePointerUp = (event: PointerEvent<HTMLDivElement>) => {
    if (pointerIdRef.current !== event.pointerId) return;
    const wasDrag = didDragRef.current;

    if (!wasDrag || reduceMotionRef.current) {
      inertiaVelocityRef.current = 0;
    }

    releasePointer();
    applyOffset(offsetRef.current);
    if (wasDrag) suppressClickRef.current = true;
  };

  const handlePointerCancel = (event: PointerEvent<HTMLDivElement>) => {
    if (pointerIdRef.current !== event.pointerId) return;
    if (didDragRef.current) suppressClickRef.current = true;
    inertiaVelocityRef.current = 0;
    releasePointer();
    applyOffset(offsetRef.current);
  };

  const handleClickCapture = (event: MouseEvent<HTMLDivElement>) => {
    if (!suppressClickRef.current) return;
    event.preventDefault();
    event.stopPropagation();
    suppressClickRef.current = false;
  };

  return (
    <div
      ref={viewportRef}
      className={[
        "relative cursor-grab overflow-hidden select-none touch-pan-y active:cursor-grabbing",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      role="region"
      aria-label={ariaLabel}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
      onClickCapture={handleClickCapture}
      onLostPointerCapture={() => {
        isDraggingRef.current = false;
        dragStateRef.current = null;
        pointerIdRef.current = null;
      }}
    >
      <div
        ref={trackRef}
        className={[
          "flex w-max will-change-transform",
          gapClassName,
          trackClassName,
        ]
          .filter(Boolean)
          .join(" ")}
        style={{ transform: "translate3d(0, 0, 0)" }}
      >
        {Array.from({ length: COPY_COUNT }, (_, copyIndex) => (
          <div
            key={`marquee-copy-${copyIndex}`}
            ref={copyIndex === 0 ? measureRef : undefined}
            className={["flex shrink-0", gapClassName].join(" ")}
            aria-hidden={copyIndex === 0 ? undefined : true}
          >
            {children}
          </div>
        ))}
      </div>
    </div>
  );
}
